import { useRef, useState } from 'react';
import { Upload, X } from 'phosphor-react';

interface LogoUploadProps {
  label: string;
  preview: string;
  file: File | null;
  onFileChange: (file: File | null) => void;
  defaultImage?: string;
}

export function LogoUpload({ label, preview, file, onFileChange }: LogoUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (!selectedFile.type.startsWith('image/')) {
        return;
      }
      onFileChange(selectedFile);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      if (droppedFile.type.startsWith('image/')) {
        onFileChange(droppedFile);
      }
    }
  };

  const handleRemove = () => {
    onFileChange(null);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-3">
      <label className="text-sm font-medium theme-text">{label}</label>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
      />
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        className={`relative w-full rounded-xl border-2 border-dashed transition-all ${isDragging
            ? 'border-[color:var(--nt-primary)] bg-[color:color-mix(in_oklab,var(--card-bg)_78%,var(--nt-primary)_22%)]'
            : 'theme-border hover:border-[color:var(--nt-primary)]/50'
          } ${preview ? 'p-3' : 'p-6'}`}
      >
        {preview ? (
          <div className="flex items-center gap-3">
            <div className={`${label.includes('Mini') ? 'w-12 h-12' : 'w-16 h-12'} rounded-lg border theme-border bg-white dark:bg-[color:var(--card-bg)] flex items-center justify-center p-2 flex-shrink-0`}>
              <img src={preview} alt={label} className="max-w-full max-h-full object-contain" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium theme-text truncate">
                {file?.name || label}
              </div>
              <div className="text-xs theme-text-muted">
                {file ? `${(file.size / 1024).toFixed(1)} KB` : 'Imagem carregada'}
              </div>
            </div>
            <button
              type="button"
              onClick={handleRemove}
              className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center theme-text-muted hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="w-full flex flex-col items-center justify-center gap-2"
          >
            <div className="w-10 h-10 rounded-lg border theme-border bg-[color:color-mix(in_oklab,var(--card-bg)_50%,transparent)] flex items-center justify-center">
              <Upload className="w-5 h-5 theme-text-muted" />
            </div>
            <div className="text-center">
              <div className="text-sm font-medium theme-text">Clique ou arraste</div>
              <div className="text-xs theme-text-muted">PNG, JPG até 5MB</div>
            </div>
          </button>
        )}
      </div>
    </div>
  );
}
